/**
 * Custom ScrollBar Component
 *
 * カスタムスクロールバーを提供するコンポーネント
 * - 外部からスクロール可能な高さ（scrollHeight, clientHeight）を受け取る
 * - thumbをドラッグして移動できる
 * - スクロール位置の変更をCustomEventで通知する
 */

export class ScrollBar {
  private container: HTMLElement;
  private track: HTMLElement;
  private thumb: HTMLElement;

  private isDragging: boolean = false;
  private dragStartY: number = 0;
  private dragStartTop: number = 0;

  // スクロール情報
  private scrollHeight: number = 0;
  private clientHeight: number = 0;
  private scrollTop: number = 0;

  constructor(container: HTMLElement) {
    this.container = container;

    // 既存のトラックとthumbを取得
    this.track = this.container;
    const thumbElement = this.container.querySelector('.js-scroll-bar-thumb') as HTMLElement;

    if (!thumbElement) {
      throw new Error('ScrollBar: .js-scroll-bar-thumb not found');
    }

    this.thumb = thumbElement;

    this.bindEvents();
  }

  /**
   * イベントをバインド
   */
  private bindEvents(): void {
    // Thumbのドラッグ開始
    this.thumb.addEventListener('mousedown', this.handleMouseDown.bind(this));

    // ドキュメント全体でマウス移動とマウスアップを監視
    document.addEventListener('mousemove', this.handleMouseMove.bind(this));
    document.addEventListener('mouseup', this.handleMouseUp.bind(this));

    // トラックのクリック（thumbがない場所をクリックした時）
    this.track.addEventListener('mousedown', this.handleTrackClick.bind(this));
  }

  /**
   * マウスダウン（ドラッグ開始）
   */
  private handleMouseDown(e: MouseEvent): void {
    e.preventDefault();
    e.stopPropagation(); // トラッククリックと区別するため

    this.isDragging = true;
    this.dragStartY = e.clientY;
    this.dragStartTop = this.thumb.offsetTop;

    this.thumb.classList.add('is-dragging');
  }

  /**
   * マウス移動（ドラッグ中）
   */
  private handleMouseMove(e: MouseEvent): void {
    if (!this.isDragging) return;

    e.preventDefault();

    const deltaY = e.clientY - this.dragStartY;
    const newTop = this.dragStartTop + deltaY;

    // トラックの範囲内に制限
    const maxTop = this.track.clientHeight - this.thumb.clientHeight;
    const clampedTop = Math.max(0, Math.min(newTop, maxTop));

    // thumbの位置を更新
    this.thumb.style.top = `${clampedTop}px`;

    // スクロール位置を計算してイベントを発火
    const scrollPercentage = clampedTop / maxTop;
    const newScrollTop = scrollPercentage * (this.scrollHeight - this.clientHeight);

    this.dispatchScrollEvent(newScrollTop, scrollPercentage);
  }

  /**
   * マウスアップ（ドラッグ終了）
   */
  private handleMouseUp(e: MouseEvent): void {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.thumb.classList.remove('is-dragging');
  }

  /**
   * トラッククリック（thumbがない場所をクリック）
   */
  private handleTrackClick(e: MouseEvent): void {
    // thumbをクリックした場合は無視
    if (e.target === this.thumb) return;

    e.preventDefault();

    const trackRect = this.track.getBoundingClientRect();
    const clickY = e.clientY - trackRect.top;

    // thumbの中心がクリック位置に来るように計算
    const thumbHeight = this.thumb.clientHeight;
    const newTop = clickY - (thumbHeight / 2);

    // トラックの範囲内に制限
    const maxTop = this.track.clientHeight - thumbHeight;
    const clampedTop = Math.max(0, Math.min(newTop, maxTop));

    // thumbの位置を更新
    this.thumb.style.top = `${clampedTop}px`;

    // スクロール位置を計算してイベントを発火
    const scrollPercentage = clampedTop / maxTop;
    const newScrollTop = scrollPercentage * (this.scrollHeight - this.clientHeight);

    this.dispatchScrollEvent(newScrollTop, scrollPercentage);
  }

  /**
   * スクロール情報を更新（外部から呼ばれる）
   * @param scrollHeight - 全体の高さ
   * @param clientHeight - 表示領域の高さ
   * @param scrollTop - 現在のスクロール位置
   */
  public updateScroll(scrollHeight: number, clientHeight: number, scrollTop: number): void {
    this.scrollHeight = scrollHeight;
    this.clientHeight = clientHeight;
    this.scrollTop = scrollTop;

    // スクロール可能かどうかチェック
    const isScrollable = scrollHeight > clientHeight;

    if (!isScrollable) {
      this.container.classList.add('is-disabled');
    } else {
      this.container.classList.remove('is-disabled');
    }

    // スクロール可能な場合のみthumbの高さと位置を更新
    if (isScrollable) {
      // thumbの高さを計算（表示領域 / 全体の高さ の比率）
      const thumbHeight = (clientHeight / scrollHeight) * this.track.clientHeight;
      this.thumb.style.height = `${thumbHeight}px`;

      // thumbの位置を計算（現在のスクロール位置の比率）
      const maxScrollTop = scrollHeight - clientHeight;
      const scrollPercentage = scrollTop / maxScrollTop;
      const maxThumbTop = this.track.clientHeight - thumbHeight;
      const thumbTop = scrollPercentage * maxThumbTop;

      // ドラッグ中でない場合のみ位置を更新
      if (!this.isDragging) {
        this.thumb.style.top = `${thumbTop}px`;
      }
    }
  }

  /**
   * スクロールイベントを発火
   * @param scrollTop - スクロール位置
   * @param scrollPercentage - スクロール割合（0〜1）
   */
  private dispatchScrollEvent(scrollTop: number, scrollPercentage: number): void {
    const event = new CustomEvent('scrollbar:change', {
      detail: {
        scrollTop: scrollTop,
        scrollPercentage: scrollPercentage
      }
    });

    this.container.dispatchEvent(event);
  }

  /**
   * 破棄
   */
  public destroy(): void {
    this.thumb.removeEventListener('mousedown', this.handleMouseDown.bind(this));
    document.removeEventListener('mousemove', this.handleMouseMove.bind(this));
    document.removeEventListener('mouseup', this.handleMouseUp.bind(this));
    this.track.removeEventListener('mousedown', this.handleTrackClick.bind(this));

    this.container.removeChild(this.track);
  }
}
